import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const Pagination = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange
}) => {
  const { isDarkMode } = useTheme();

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const pages = generatePageNumbers();

  // Tailwind dark classes
  const bg = isDarkMode ? 'bg-[#232b3b] border-[#232b3b]' : 'bg-white border-gray-200';
  const text = isDarkMode ? 'text-gray-100' : 'text-gray-700';
  const border = isDarkMode ? 'border-[#232b3b]' : 'border-gray-300';
  const hoverBg = isDarkMode ? 'hover:bg-[#2d3750]' : 'hover:bg-gray-50';
  const activeBg = isDarkMode
    ? 'bg-primary-600 border-primary-500 text-white'
    : 'bg-primary-50 border-primary-500 text-primary-600';

  return (
    <div className={`flex items-center justify-between px-4 py-3 ${bg} border-t sm:px-6`}>
      <div className="flex items-center justify-between flex-1 sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${text} ${bg} ${border} rounded-md ${hoverBg} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          Précédent
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium ${text} ${bg} ${border} rounded-md ${hoverBg} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          Suivant
        </button>
      </div>

      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div className="flex items-center space-x-2">
          <p className={`text-sm ${text}`}>
            Affichage de{' '}
            <span className="font-medium">
              {((currentPage - 1) * pageSize) + 1}
            </span>{' '}
            à{' '}
            <span className="font-medium">
              {Math.min(currentPage * pageSize, totalItems)}
            </span>{' '}
            sur{' '}
            <span className="font-medium">{totalItems}</span>{' '}
            résultats
          </p>

          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
            className={`ml-4 text-sm ${border} rounded px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-transparent ${bg} ${text}`}
          >
            <option value={10}>10 par page</option>
            <option value={25}>25 par page</option>
            <option value={50}>50 par page</option>
            <option value={100}>100 par page</option>
          </select>
        </div>

        <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm">
          {/* Previous button */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-2 py-2 text-sm font-medium ${text} ${bg} ${border} rounded-l-md ${hoverBg} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          {/* First page */}
          {pages[0] > 1 && (
            <>
              <button
                onClick={() => onPageChange(1)}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${text} ${bg} ${border} ${hoverBg}`}
              >
                1
              </button>
              {pages[0] > 2 && (
                <span className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${text} ${bg} ${border}`}>
                  ...
                </span>
              )}
            </>
          )}

          {/* Page numbers */}
          {pages.map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border transition-all ${
                page === currentPage
                  ? `z-10 ${activeBg} shadow-lg`
                  : `${bg} ${border} ${text} ${hoverBg}`
              }`}
              style={page === currentPage && isDarkMode ? { boxShadow: '0 0 0 2px #fff' } : {}}
            >
              {page}
            </button>
          ))}

          {/* Last page */}
          {pages[pages.length - 1] < totalPages && (
            <>
              {pages[pages.length - 1] < totalPages - 1 && (
                <span className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${text} ${bg} ${border}`}>
                  ...
                </span>
              )}
              <button
                onClick={() => onPageChange(totalPages)}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${text} ${bg} ${border} ${hoverBg}`}
              >
                {totalPages}
              </button>
            </>
          )}

          {/* Next button */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`relative inline-flex items-center px-2 py-2 text-sm font-medium ${text} ${bg} ${border} rounded-r-md ${hoverBg} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </nav>
      </div>
    </div>
  );
};

export default Pagination;